import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatChip } from '../utils/formatTime';

const POPOVER_WIDTH = 240;
const POPOVER_ESTIMATE_HEIGHT = 320;

function ShiftPickerPopover({ pickerState, shift, shiftTemplates, templateMap, isSaving, onPick, onClose }) {
  const { t } = useTranslation('scheduler');
  const popoverRef = useRef(null);
  const [mode, setMode] = useState('pick');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customError, setCustomError] = useState('');

  useEffect(() => {
    if (pickerState) {
      setMode('pick');
      setCustomStart('');
      setCustomEnd('');
      setCustomError('');
    }
  }, [pickerState]);

  useEffect(() => {
    if (!pickerState) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [pickerState, onClose]);

  useEffect(() => {
    if (!pickerState) return;
    const handleMouseDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [pickerState, onClose]);

  if (!pickerState) return null;

  const { anchorRect } = pickerState;

  let top = anchorRect.bottom + 6;
  if (top + POPOVER_ESTIMATE_HEIGHT > window.innerHeight) {
    top = anchorRect.top - POPOVER_ESTIMATE_HEIGHT - 6;
    if (top < 8) top = 8;
  }

  let left = anchorRect.left;
  if (left + POPOVER_WIDTH > window.innerWidth - 8) {
    left = window.innerWidth - POPOVER_WIDTH - 8;
  }
  if (left < 8) left = 8;

  const handleCustomSave = (e) => {
    e.preventDefault();
    if (customStart === customEnd) {
      setCustomError(t('picker.customTimeForm.errorEqual'));
      return;
    }
    onPick({ type: 'custom', startTime: `${customStart}:00`, endTime: `${customEnd}:00` });
  };

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={t('picker.title')}
      className="fixed z-50 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
      style={{ top, left, width: POPOVER_WIDTH }}
    >
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
          {t('picker.title')}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-1 focus:ring-orange-200 rounded"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {isSaving && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
          <svg
            className="w-3 h-3 animate-spin text-amber-500 flex-shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
          </svg>
          <p className="text-xs text-amber-700 font-medium">{t('picker.savingPrevious')}</p>
        </div>
      )}

      {mode === 'pick' ? (
        <div className="py-1 max-h-72 overflow-y-auto">
          {shiftTemplates.map((tpl) => {
            const isSelected = !shift?.isOff && shift?.shiftTemplateId === tpl.id;
            const chip = tpl.startTime && tpl.endTime ? formatChip(tpl.startTime, tpl.endTime) : '';
            const color = tpl.colorHex || '#94A3B8';
            const label = tpl.label || tpl.name || tpl.shiftName || '';
            return (
              <button
                key={tpl.id}
                type="button"
                disabled={isSaving}
                onClick={() => onPick({ type: 'template', templateId: tpl.id })}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isSelected ? 'bg-orange-50' : ''
                }`}
              >
                <span
                  className="flex-shrink-0 rounded-sm"
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: color,
                    border: '0.5px solid rgba(0,0,0,0.12)',
                  }}
                />
                <span className="flex-1 min-w-0">
                  <span
                    className={`block text-sm font-medium truncate ${
                      isSelected ? 'text-orange-700' : 'text-gray-800'
                    }`}
                  >
                    {label}
                  </span>
                  {chip && (
                    <span className="block text-xs text-gray-400 truncate">{chip}</span>
                  )}
                </span>
                {isSelected && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 text-orange-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}

          <div className="my-1 border-t border-gray-100" />

          {/* Custom time */}
          <button
            type="button"
            disabled={isSaving}
            onClick={() => setMode('custom')}
            className="w-full flex items-center px-4 py-2.5 text-left hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-sm text-gray-600">{t('picker.customTime')}</span>
          </button>

          {/* Off */}
          <button
            type="button"
            disabled={isSaving}
            onClick={() => onPick({ type: 'off' })}
            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              shift?.isOff ? 'bg-gray-50' : ''
            }`}
          >
            <span
              className={`text-sm flex-1 ${
                shift?.isOff ? 'font-medium text-gray-700' : 'text-gray-600'
              }`}
            >
              {t('picker.markOff')}
            </span>
            {shift?.isOff && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-gray-500 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>

          {/* Remove — only when a shift exists */}
          {shift && (
            <>
              <div className="my-1 border-t border-gray-100" />
              <button
                type="button"
                disabled={isSaving}
                onClick={() => onPick({ type: 'remove' })}
                className="w-full flex items-center px-4 py-2.5 text-left hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-sm text-red-500">{t('picker.removeShift')}</span>
              </button>
            </>
          )}
        </div>
      ) : (
        <form onSubmit={handleCustomSave} className="px-4 py-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('picker.customTimeForm.startLabel')}
              </label>
              <input
                type="time"
                required
                value={customStart}
                onChange={(e) => { setCustomStart(e.target.value); setCustomError(''); }}
                className="w-full px-2.5 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 text-sm outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('picker.customTimeForm.endLabel')}
              </label>
              <input
                type="time"
                required
                value={customEnd}
                onChange={(e) => { setCustomEnd(e.target.value); setCustomError(''); }}
                className="w-full px-2.5 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 text-sm outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-500"
              />
            </div>
          </div>

          {customError && (
            <p className="text-xs text-red-600" role="alert">{customError}</p>
          )}

          <div className="flex items-center gap-2 pt-0.5">
            <button
              type="button"
              onClick={() => { setMode('pick'); setCustomError(''); }}
              className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              {t('picker.customTimeForm.back')}
            </button>
            <button
              type="submit"
              className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 text-xs font-semibold text-white shadow-sm transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300"
            >
              {t('picker.customTimeForm.save')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default ShiftPickerPopover;
